<!-- src/app/settings/accruals/pages/leave-policy-detail/leave-policy-detail.component.html -->
<div class="leave-policy-detail-container">
  <!-- Loading -->
  <div *ngIf="isLoading" class="spinner-container">
    <div class="spinner"></div>
  </div>

  <!-- Error -->
  <div *ngIf="errorMessage && !isLoading" class="error-message">
    {{ errorMessage }}
  </div>

  <ng-container *ngIf="!isLoading && policy">
    <!-- Header -->
    <div class="header">
      <div class="header-left">
        <button class="back-btn" type="button" (click)="goBack()">← Back</button>
        <div>
          <h2>{{ pageTitle }}</h2>
          <div class="header-meta">
            <span class="leave-type-badge" [ngClass]="leaveTypeClass">
              {{ policy.leaveType }}
            </span>
            <span class="status-badge active" *ngIf="policy.isActive">Active</span>
          </div>
        </div>
      </div>

      <div class="header-actions">
        <button class="btn btn-primary" (click)="openAssignPolicyModal()">
          Assign Employees
        </button>
      </div>
    </div>

    <!-- Details -->
    <div class="details-grid">
      <div class="detail-card overview-card" [ngClass]="leaveTypeClass">
        <div class="card-header">
          <h3>Policy Overview</h3>
        </div>

        <div class="detail-list">
          <div class="detail-row">
            <span class="label">Policy Name</span>
            <span class="value">{{ policy.name }}</span>
          </div>

          <div class="detail-row">
            <span class="label">Leave Type</span>
            <span class="value">{{ policy.leaveType }}</span>
          </div>

          <div class="detail-row">
            <span class="label">Accrual Frequency</span>
            <span class="value">{{ policy.accrualFrequency }}</span>
          </div>

          <div class="detail-row">
            <span class="label">Weekly Accrual Day</span>
            <span class="value">{{ policy.weeklyAccrualDay || 'N/A' }}</span>
          </div>
        </div>
      </div>

      <div class="detail-card balance-card" [ngClass]="leaveTypeClass">
        <div class="card-header">
          <h3>Balance Rules</h3>
        </div>

        <div class="detail-list">
          <div class="detail-row">
            <span class="label">Hours Per Year</span>
            <span class="value">{{ policy.hoursPerYear }}</span>
          </div>

          <div class="detail-row">
            <span class="label">Max Balance</span>
            <span class="value">{{ policy.maxBalance }}</span>
          </div>

          <div class="detail-row">
            <span class="label">Max Carry Over</span>
            <span class="value">{{ policy.maxCarryOver }}</span>
          </div>

          <div class="detail-row">
            <span class="label">Allow Negative Balance</span>
            <span class="value">
              {{ policy.allowNegativeBalance ? 'Yes' : 'No' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Assigned Employees -->
    <div class="assigned-section">
      <div class="section-header">
        <div class="section-title-wrap">
          <h3>Currently Assigned Employees</h3>
          <span class="section-count">{{ policy.assignedEmployees.length }}</span>
        </div>
      </div>

      <div class="assigned-list" *ngIf="policy.assignedEmployees.length > 0; else noAssignedEmployees">
        <div *ngFor="let employee of policy.assignedEmployees" class="assigned-row">
          <div class="assigned-info">
            <div class="assigned-top-row">
              <div class="assigned-name">{{ employee.employeeName }}</div>
              <span class="assigned-pill">Assigned</span>
            </div>

            <div class="assigned-meta">
              <span>Employee ID: {{ employee.employeeId }}</span>
              <span>Start Date: {{ employee.startDate }}</span>
            </div>
          </div>
        </div>
      </div>

      <ng-template #noAssignedEmployees>
        <div class="empty-state">
          No employees are currently assigned to this policy.
        </div>
      </ng-template>
    </div>
  </ng-container>

  <!-- Assign Policy Modal -->
  <div *ngIf="assignModalOpen" class="modal-backdrop" (click)="closeAssignPolicyModal()">
    <div class="modal assign-modal" (click)="$event.stopPropagation()">
      <h2>Assign Employees</h2>

      <p class="modal-text" *ngIf="policy">
        Assign <strong>{{ policy.name }}</strong> to one or more employees.
      </p>

      <div class="assignment-form">
        <label class="form-field">
          <span>Start Date</span>
          <input type="date" [(ngModel)]="assignmentStartDate" />
        </label>

        <div class="assignment-filters">
          <label class="filter-field search-field">
            <span>Search Employee</span>
            <input
              type="text"
              [ngModel]="assignmentFilters.searchTerm"
              (ngModelChange)="onAssignmentSearchChange($event)"
              placeholder="Search name, employee #, or email"
            />
          </label>

          <label class="filter-field">
            <span>Company</span>
            <select
              [ngModel]="assignmentFilters.companyId"
              (ngModelChange)="onAssignmentCompanyChange($event)"
            >
              <option [ngValue]="null">All companies</option>
              <option *ngFor="let company of userFilterValues?.companies" [ngValue]="company.id">
                {{ company.name }}
              </option>
            </select>
          </label>

          <label class="filter-field">
            <span>Department</span>
            <select
              [ngModel]="assignmentFilters.departmentId"
              (ngModelChange)="onAssignmentDepartmentChange($event)"
            >
              <option [ngValue]="null">All departments</option>
              <option *ngFor="let dept of userFilterValues?.departments" [ngValue]="dept.id">
                {{ dept.code }} - {{ dept.name }}
              </option>
            </select>
          </label>

          <div class="filter-actions">
            <button
              class="clear-filters-btn"
              type="button"
              (click)="clearAssignmentFilters()"
              [disabled]="!hasAssignmentFilters"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div class="assignment-summary">
          {{ assignmentSelectedCount }} employee{{ assignmentSelectedCount === 1 ? '' : 's' }} selected
        </div>

        <div class="employee-select-list">
          <div
            *ngFor="let employee of filteredAssignableUsers"
            class="employee-select-row"
            [class.selected]="isEmployeeSelected(employee.id)"
            (click)="toggleEmployeeSelection(employee.id)"
          >
            <div class="employee-select-info">
              <div class="employee-name-row">
                <div class="employee-name">
                  {{ employee.firstName }} {{ employee.lastName }}
                </div>
                <div class="employee-number">
                  #{{ employee.employeeNumber }}
                </div>
              </div>

              <div class="employee-meta">
                <span>{{ employee.company?.name || 'No company' }}</span>
                <span>{{ employee.department?.code || '—' }} {{ employee.department?.name || '' }}</span>
                <span *ngIf="employee.email">{{ employee.email }}</span>
              </div>
            </div>

            <div class="employee-select-check">
              <input
                type="checkbox"
                [checked]="isEmployeeSelected(employee.id)"
                (click)="$event.stopPropagation()"
                (change)="toggleEmployeeSelection(employee.id)"
              />
            </div>
          </div>

          <div *ngIf="filteredAssignableUsers.length === 0" class="empty-state compact">
            No employees match the current filters.
          </div>
        </div>
      </div>

      <div class="modal-buttons">
        <button
          type="button"
          class="btn btn-secondary"
          (click)="closeAssignPolicyModal()"
          [disabled]="isAssigningPolicy"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn btn-primary"
          (click)="assignSelectedEmployees()"
          [disabled]="isAssigningPolicy"
        >
          {{ isAssigningPolicy ? 'Saving...' : 'Assign Policy' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Toast -->
  <div *ngIf="toastVisible" class="toast" [ngClass]="toastType">
    <img class="icon" alt="icon" />
    <span>{{ toastMessage }}</span>
  </div>
</div>